import { useTranslation } from 'react-i18next';
import { Input, Label } from '@attendance/ui';

// Mirror the server's BrandingSettingsSchema color rule (`/^#[0-9A-Fa-f]{6}$/`)
// so a 3-char or "#GGG" value is caught client-side with inline guidance
// instead of bouncing off the API with a 422 (P2-2).
export const HEX_COLOR_RE = /^#[0-9A-Fa-f]{6}$/;

export function ColorField({
  label,
  value,
  onChange,
  invalid,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean;
}): JSX.Element {
  // The native <input type="color"> requires a valid 6-digit hex; feed it a
  // safe fallback while the text field holds an in-progress / invalid value so
  // the swatch doesn't reset the typed text.
  const { t } = useTranslation();
  const swatchValue = HEX_COLOR_RE.test(value) ? value : '#000000';
  return (
    <div className="space-y-element">
      <Label htmlFor={`color-${label}`}>{label}</Label>
      <div className="flex items-center gap-inline">
        <input
          id={`color-${label}`}
          type="color"
          value={swatchValue}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-12 rounded-control border border-border p-0"
        />
        <Input
          // The swatch beside it owns `color-${label}`; this box had no
          // accessible name of its own, so a screen reader announced only
          // "edit text" for the field that actually takes the hex.
          aria-label={t('settings.branding.color.hexValueLabel', '{{label}} hex value', { label })}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={7}
          // `error` derives `aria-invalid` AND the danger border from the
          // shared control skin, so the hand-written border override is gone;
          // `aria-describedby` is what supplies the reason.
          error={invalid}
          aria-describedby={invalid ? `color-${label}-error` : undefined}
          className="font-mono uppercase"
        />
      </div>
      {invalid && (
        <p id={`color-${label}-error`} className="text-copy-sm text-danger" role="alert">
          {t('settings.branding.color.hexHint', 'Use a 6-digit hex like #6C47FF.')}
        </p>
      )}
    </div>
  );
}
